import * as React from "react";
import { useIntl } from "react-intl";
import { Dropdown, Grid, Menu } from "semantic-ui-react";

import { useCategories } from "@/utils";

import { PromptServices } from "@/services";

import PromptsList from "./components/PromptList";

import "./styles.scss";
import { descriptors, LibraryType } from "./descriptors";
import { fromPrompt, SharedPostFlowDefaults } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { PlayerPostDialog } from "@/components/PostDialogs/PlayerPostDialog";
import { SearchComponent } from "@/components/Search";
import { useMemo } from "react";
import { PromptCategoryCards } from "@/components/PromptCategoryCards";
import { PlayerPrompt, Prompt } from "@/interfaces";

interface OwnProps {
  coachId: string | number;
  playerId: number;
  reloadPrompts?: boolean;
}

type Props = OwnProps;

const PlayerLibraryListContentFC: React.FC<Props> = ({ reloadPrompts = false, playerId }) => {
  const { formatMessage } = useIntl();
  const [promptList, setPromptList] = React.useState<PlayerPrompt[]>([]);
  const [viewType, setViewType] = React.useState<"LIST" | "CARD">(
    localStorage.getItem("libraryViewType") == "CARD" ? "CARD" : "LIST"
  );

  const [showPostModal, setShowPostModal] = React.useState<boolean>(false);
  const [selectedPrompt, setSelectedPrompt] = React.useState<Prompt | undefined>(undefined);

  const { categories, categoryOptions } = useCategories("LIBRARY");

  const content = useMemo(() => {
    if (viewType == "LIST") {
      return <PromptsList categories={categories} promptList={promptList} />;
    } else {
      return (
        <PromptCategoryCards
          categories={categories}
          promptList={promptList.map(p => p.prompt)}
          onClickPrompt={prompt => {
            setSelectedPrompt(prompt);
            setShowPostModal(true);
          }}
        />
      );
    }
  }, [viewType, categories, promptList, setSelectedPrompt, setShowPostModal]);

  const postDefaults: SharedPostFlowDefaults | undefined = React.useMemo(() => {
    return selectedPrompt ? fromPrompt(selectedPrompt) : undefined;
  }, [selectedPrompt]);

  return (
    <Grid data-elm-id={"playerLibraryListContent"}>
      <Grid.Row>
        <Grid.Column>
          <SearchComponent
            cacheKey={"playerPrompts"}
            refreshData={setPromptList}
            forceRefresh={reloadPrompts}
            content={content}
            customCoachId={`${playerId}`}
            filterPlaceholder={formatMessage({ ...descriptors[LibraryType.byCategory] })}
            additionalFilterContent={
              <Menu.Item>
                <Dropdown
                  selection
                  placeholder={"View Type"}
                  className="searchInput"
                  selectOnBlur={false}
                  onChange={(_, data) => {
                    localStorage.setItem("libraryViewType", data.value as "LIST" | "CARD");
                    setViewType(data.value as "LIST" | "CARD");
                  }}
                  value={viewType}
                  options={[
                    { text: "List View", value: "LIST", icon: "list" },
                    { text: "Card View", value: "CARD", icon: "block layout" },
                  ]}
                />
              </Menu.Item>
            }
            searchFunction={PromptServices.playerLibrary}
            showSortBy={false}
            filters={categoryOptions}
            pageable={false}
          />
          {showPostModal && postDefaults && (
            <PlayerPostDialog
              open={showPostModal}
              defaults={postDefaults}
              handleClose={() => {
                setShowPostModal(false);
                setSelectedPrompt(undefined);
              }}
            />
          )}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export const PlayerPrompts = PlayerLibraryListContentFC;
